import { type RefObject, useEffect, useRef } from "react";

interface UseInfiniteScrollOptions {
    hasMore: boolean;
    isFetchingMore: boolean;
    onLoadMore: () => void;
    threshold?: number;
}

export const useInfiniteScroll = <T extends Element>({
    hasMore,
    isFetchingMore,
    onLoadMore,
    threshold = 0.1,
}: UseInfiniteScrollOptions): RefObject<T> => {
    const sentinelRef = useRef<T>(null);

    useEffect(() => {
        const el = sentinelRef.current;
        if (!el || !hasMore || isFetchingMore) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    onLoadMore();
                }
            },
            { threshold },
        );
        observer.observe(el);
        return () => {
            observer.disconnect();
        };
    }, [hasMore, isFetchingMore, onLoadMore, threshold]);

    return sentinelRef;
};
